import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { X, Send, Loader2, Headset, Check, ArrowLeft, Bot } from "lucide-react";
import { ChatAttach } from "@/components/ChatAttach";
import axios from "axios";
import { API } from "@/data/catalog";

const LEAD = "[[LEAD_FORM]]";
const SID_KEY = "if_chat_sid";
const TOKEN_KEY = "if_chat_token";
const getSid = () => {
  let s = localStorage.getItem(SID_KEY);
  if (!s) { s = `cs-${crypto.randomUUID()}`; localStorage.setItem(SID_KEY, s); }
  return s;
};
const getToken = () => localStorage.getItem(TOKEN_KEY) || "";

// Markdown minimale: titoli, grassetto, elenchi, paragrafi
const Md = ({ text }) => {
  const lines = text.replace(LEAD, "").split("\n");
  const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith("**") ? <strong key={i} className="font-semibold text-white">{p.slice(2, -2)}</strong> : p);
  const out = []; let list = [];
  const flush = () => { if (list.length) { out.push(<ul key={`l${out.length}`} className="list-disc pl-4 space-y-1 my-1.5">{list}</ul>); list = []; } };
  lines.forEach((ln, i) => {
    const t = ln.trim();
    if (!t || t === "---") { flush(); return; }
    if (/^[-•*] /.test(t)) { list.push(<li key={i}>{inline(t.slice(2))}</li>); return; }
    flush();
    if (/^#{1,3} /.test(t)) out.push(<p key={i} className="font-bold text-[#FFD54A] mt-2 mb-0.5">{inline(t.replace(/^#+ /, ""))}</p>);
    else out.push(<p key={i} className="my-1">{inline(t)}</p>);
  });
  flush();
  return <div className="text-[13px] leading-relaxed">{out}</div>;
};

const LeadForm = ({ sid, onDone, t }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", business: "", message: "", consent: false, website: "" });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const ok = f.name.trim().length >= 2 && /.+@.+\..+/.test(f.email) && f.phone.trim().length >= 5 && f.business.trim().length >= 2 && f.consent;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await axios.post(`${API}/chat/lead`, { ...f, session_id: sid }); onDone(); }
    catch (er) { setErr(typeof er.response?.data?.detail === "string" ? er.response.data.detail : t("chat.leadErr")); }
    finally { setBusy(false); }
  };
  const ic = "w-full bg-white/10 border border-white/15 rounded-xl px-3 py-2 text-[13px] text-white placeholder:text-slate-400 focus:outline-none focus:border-[#FFD54A]/70";
  return (
    <form onSubmit={submit} className="mt-2 rounded-2xl border border-[#FFD54A]/30 bg-[#0F1F4D] p-3.5 space-y-2" data-testid="chat-lead-form">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FFD54A] flex items-center gap-1.5"><Headset className="w-3.5 h-3.5" /> {t("chat.leadTitle")}</p>
      <p className="text-[12px] text-slate-300 leading-snug">{t("chat.leadSub")}</p>
      <input className={ic} placeholder={t("chat.leadName")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="lead-name" />
      <div className="grid grid-cols-2 gap-2">
        <input className={ic} type="email" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="lead-email" />
        <input className={ic} placeholder={t("chat.leadPhone")} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="lead-phone" />
      </div>
      <input className={ic} placeholder={t("chat.leadBusiness")} value={f.business} onChange={(e) => setF({ ...f, business: e.target.value })} data-testid="lead-business" />
      <textarea className={`${ic} resize-none`} rows={2} placeholder={t("chat.leadMsg")} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="lead-message" />
      <input type="text" name="website" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: -9999, width: 1, height: 1 }} />
      <label className="flex items-start gap-2 text-[11px] text-slate-300 leading-snug cursor-pointer">
        <input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-0.5 accent-[#FFD54A]" data-testid="lead-consent" />
        <span>{t("chat.leadConsent")} <a href="/legale/privacy" target="_blank" rel="noreferrer" className="underline text-[#FFD54A]">Privacy Policy</a>.</span>
      </label>
      {err && <p className="text-[11px] text-red-300" data-testid="lead-error">{err}</p>}
      <button type="submit" disabled={!ok || busy} data-testid="lead-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-[#FFD54A] hover:bg-[#FFE27A] disabled:opacity-40 text-[#0F172A] text-[12px] font-bold py-2.5 rounded-full transition-colors">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {t("chat.leadSend")}
      </button>
    </form>
  );
};

export const ChatWidget = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { const show = () => setOpen(true); window.addEventListener("instant-open-chat", show); return () => window.removeEventListener("instant-open-chat", show); }, []);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [unread, setUnread] = useState(false);
  const [files, setFiles] = useState([]);
  const [showCats, setShowCats] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [maxMb, setMaxMb] = useState(500);
  const bodyRef = useRef(null);
  const sid = getSid();
  useEffect(() => { axios.get(`${API}/chat/upload/config`).then((r) => setMaxMb(r.data.max_mb)).catch(() => {}); }, []);

  useEffect(() => {
    const tok = getToken();
    if (!tok) return; // no bound session yet → nothing to restore
    axios.get(`${API}/chat/history/${sid}`, { headers: { "X-Chat-Token": tok } }).then((r) => setMsgs(r.data.messages || [])).catch(() => {});
  }, [sid]);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [msgs, leadOpen, open]);

  if (pathname.startsWith("/admin") || pathname.startsWith("/studio")) return null;

  const send = async (text) => {
    let q = (text || input).trim();
    if ((!q && !files.length) || busy) return;
    if (!q) q = t("chat.analyzeFiles");
    const shown = files.length ? `${q}\n📎 ${files.map((f) => f.name).join(", ")}` : q;
    setInput(""); setBusy(true); setShowCats(false); setFiles([]);
    setMsgs((m) => [...m, { role: "user", content: shown }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat/message`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: q, lang: (i18n.language || "it").slice(0, 2) }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "err");
      const issued = res.headers.get("X-Chat-Token");
      if (issued) localStorage.setItem(TOKEN_KEY, issued);
      const reader = res.body.getReader(); const dec = new TextDecoder(); let acc = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        acc += dec.decode(value, { stream: true });
        setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: acc }; return c; });
      }
      if (acc.includes(LEAD)) { setLeadOpen(true); setLeadDone(false); }
      if (!open) setUnread(true);
    } catch (e) {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: typeof e.message === "string" && e.message !== "err" ? e.message : t("chat.err") }; return c; });
    } finally { setBusy(false); }
  };

  const quick = t("chat.quick", { returnObjects: true });
  const onDrop = async (e) => {
    e.preventDefault(); setDragOver(false);
    const list = e.dataTransfer?.files; if (!list?.length) return;
    const { uploadChunked } = await import("@/components/ChatAttach");
    for (const f of Array.from(list)) { try { const r = await uploadChunked(f, sid, () => {}); setFiles((v) => [...v, r]); } catch { /* mostrato dal componente */ } }
  };

  return (
    <div className="fixed bottom-4 left-4 sm:bottom-5 sm:left-5 z-[60] flex flex-col items-start" data-testid="chat-widget" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      {open && (
        <div className="mb-3 max-sm:fixed max-sm:left-4 max-sm:right-4 max-sm:bottom-24 max-sm:mb-0 w-[calc(100vw-2rem)] sm:w-[380px] h-[min(600px,calc(100vh-7rem))] max-sm:h-[min(560px,calc(100vh-9rem))] bg-[#0B1A45] text-white rounded-3xl shadow-[0_30px_80px_-20px_rgba(2,6,23,0.7)] border border-white/10 flex flex-col overflow-hidden" data-testid="chat-panel">
          <div className="px-4 py-3.5 flex items-center gap-3 border-b border-white/10" style={{ background: "linear-gradient(135deg,#122A6E,#0B1A45)" }}>
            <button type="button" onClick={() => setShowCats((v) => !v)} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full border border-white/20 hover:border-[#FFD54A] text-[11px] font-semibold text-white/85 shrink-0" data-testid="chat-categories" aria-pressed={showCats}><ArrowLeft className="w-3.5 h-3.5" /> {t("chat.categories")}</button>
            <div className="w-9 h-9 rounded-full bg-[#FFD54A] flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-[#0F172A]" /></div>
            <div className="min-w-0 flex-1">
              <Link to="/" onClick={() => setOpen(false)} className="text-sm font-bold leading-tight" data-testid="chat-home-link">🔙 Home</Link>
              <p className="text-[10px] text-[#C7D6FF] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> {t("chat.status")}</p>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Chiudi" data-testid="chat-close"><X className="w-4 h-4" /></button>
          </div>
          <div ref={bodyRef} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
            className={`flex-1 overflow-y-auto px-4 py-3 space-y-3 relative ${dragOver ? "outline outline-2 outline-dashed outline-[#FFD54A]/70 -outline-offset-4" : ""}`} data-testid="chat-messages">
            {dragOver && <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0B1A45]/80 text-[#FFD54A] text-sm font-bold pointer-events-none" data-testid="chat-dropzone">{t("chat.dropHere")}</div>}
            <div className="max-w-[92%] rounded-2xl rounded-tl-md bg-white/[0.07] px-3.5 py-2.5 text-[13px] leading-relaxed text-slate-100" data-testid="chat-welcome">{t("chat.welcome")}</div>
            {showCats && msgs.length > 0 && (
              <div className="flex flex-wrap gap-1.5 rounded-2xl border border-[#FFD54A]/30 bg-white/[0.04] p-2.5" data-testid="chat-categories-panel">
                {quick.map((q) => <button key={q} onClick={() => send(q)} className="text-[11px] font-semibold border border-[#FFD54A]/40 text-[#FFD54A] hover:bg-[#FFD54A]/10 px-2.5 py-1.5 rounded-full transition-colors">{q}</button>)}
              </div>
            )}
            {msgs.length === 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="chat-quick">
                {quick.map((q) => <button key={q} onClick={() => send(q)} className="text-[11px] font-semibold border border-[#FFD54A]/40 text-[#FFD54A] hover:bg-[#FFD54A]/10 px-2.5 py-1.5 rounded-full transition-colors">{q}</button>)}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[92%] px-3.5 py-2.5 rounded-2xl ${m.role === "user" ? "bg-[#FFD54A] text-[#0F172A] rounded-tr-md text-[13px] leading-relaxed" : "bg-white/[0.07] text-slate-100 rounded-tl-md"}`} data-testid={`chat-msg-${m.role}`}>
                  {m.role === "user" ? m.content : (m.content ? <Md text={m.content} /> : <Loader2 className="w-4 h-4 animate-spin text-[#FFD54A]" />)}
                </div>
              </div>
            ))}
            {leadOpen && !leadDone && <LeadForm sid={sid} t={t} onDone={() => { setLeadDone(true); setLeadOpen(false); }} />}
            {leadDone && <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 px-3.5 py-2.5 text-[12px] text-emerald-200 flex items-start gap-2" data-testid="chat-lead-done"><Check className="w-4 h-4 shrink-0 mt-0.5" /> {t("chat.leadDone")}</div>}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-white/10">
            <ChatAttach sid={sid} files={files} setFiles={setFiles} disabled={busy} maxMb={maxMb}>
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("chat.placeholder")} maxLength={2000} data-testid="chat-input"
              className="flex-1 bg-white/[0.08] border border-white/10 rounded-full px-4 py-2.5 text-[13px] text-white placeholder:text-slate-400 focus:outline-none focus:border-[#FFD54A]/70" />
            <button type="submit" disabled={busy || (!input.trim() && !files.length)} data-testid="chat-send" aria-label="Invia"
              className="w-10 h-10 rounded-full bg-[#FFD54A] hover:bg-[#FFE27A] disabled:opacity-40 text-[#0F172A] flex items-center justify-center transition-colors shrink-0">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
            </ChatAttach>
          </form>
          <div className="px-4 pb-2 text-[9px] text-slate-500 flex flex-wrap gap-x-2 justify-between">
            <span>{t("chat.disclaimer")}</span>
            <button type="button" onClick={() => { setLeadOpen(true); setLeadDone(false); }} className="underline hover:text-[#FFD54A]" data-testid="chat-human">{t("chat.human")}</button>
          </div>
        </div>
      )}
      <button onClick={() => { setOpen(!open); setUnread(false); }} data-testid="chat-toggle" aria-label="Assistant" title={open ? t("chat.closeLabel") : t("chat.openLabel")}
        className="if-float-pill relative text-white border border-[#D4AF37]/60 hover:-translate-y-0.5 transition-transform"
        style={{ background: "linear-gradient(135deg,#1E3A8A,#0B1A45)" }}>
        {open ? <X className="w-3 h-3 shrink-0" /> : <span className="text-[11px] leading-none" aria-hidden="true">🤖</span>} {t("chat.openLabel")}
        {unread && !open && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#FFD54A] border-2 border-[#0B1A45]" />}
      </button>
    </div>
  );
};
